import { supabase } from "@/integrations/supabase/client";

export type ForwardedEmailRow = {
  id: string;
  from_address: string;
  subject: string | null;
  body_text: string | null;
  received_at: string;
  rejection_signal: string | null;
  application_id: string | null;
};

export const forwardedEmailsQueryKey = (userId: string) =>
  ["forwarded-emails", userId] as const;

export async function fetchForwardedEmails(userId: string): Promise<ForwardedEmailRow[]> {
  const { data, error } = await supabase
    .from("forwarded_emails")
    .select(
      "id, from_address, subject, body_text, received_at, rejection_signal, application_id",
    )
    .eq("user_id", userId)
    .order("received_at", { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data ?? []) as ForwardedEmailRow[];
}

/** Best-effort extraction of company name from an email From header or subject. */
export function extractCompany(fromAddress: string, subject: string | null): string {
  // 1. Try "Display Name <addr@host>" — strip role-y suffixes like "Recruiting", "Hiring Team"
  const nameMatch = fromAddress.match(/^"?([^"<]+?)"?\s*<[^>]+>/);
  if (nameMatch) {
    const cleaned = nameMatch[1]
      .replace(/\b(recruiting|hiring|talent|team|notifications?|noreply|no-reply|auto[- ]?reply|workday[- ]?support)\b/gi, "")
      .replace(/[,@].*$/, "")
      .trim();
    if (cleaned.length >= 2) return cleaned;
  }
  // 2. Fall back to the email domain
  const addrMatch = fromAddress.match(/<([^>]+)>|([^\s<>]+@[^\s<>]+)/);
  const addr = addrMatch?.[1] ?? addrMatch?.[2] ?? fromAddress;
  const domain = addr.split("@")[1] ?? "";
  const host = domain
    .replace(/^(mail|email|no-?reply|notifications?|hire|jobs|careers|recruiting|talent)\./i, "")
    .replace(/\.(com|io|co|net|org|ai|app|dev|us|inc)$/i, "")
    .split(".")
    .pop() ?? domain;
  // Special-case ATS domains
  const ats: Record<string, string> = {
    ashbyhq: subject?.match(/at\s+([A-Z][\w&'.\- ]+)/)?.[1] ?? "Ashby",
    greenhouse: "Greenhouse",
    lever: "Lever",
    myworkday: subject?.match(/(?:from|on)\s+([A-Z][\w&'.\- ]+?)(?:'s|\s+on|\s+Sr|\s+Senior|\s+Director|\s+Job|\s+Role|$)/)?.[1] ?? "Workday",
    successfactors: "SuccessFactors",
    avature: subject?.match(/^([A-Z][\w&'.\- ]+?)(?:\s+(?:LLP|Inc|Corp|Co))?\s+(?:Follow|Update|Application|Job)/)?.[1] ?? "Avature",
    appreview: subject?.match(/(?:Following up on your|Your)\s+([A-Z][\w&'.\- ]+?)\s+(?:Staff|Senior|Sr|Junior|Jr|Lead|Principal|Director|Manager|Head)/)?.[1] ?? "Gem",
    "greenhouse-mail": subject?.split(" ")[0] ?? "Greenhouse",
  };
  if (ats[host]) return ats[host];
  // Title-case the domain root
  return host.charAt(0).toUpperCase() + host.slice(1);
}

/** Best-effort extraction of role title from the subject line. */
export function extractRole(subject: string | null): string {
  if (!subject) return "—";
  const cleaned = subject
    // Strip common subject prefixes
    .replace(/^(re:|fwd:|fw:)\s*/i, "")
    .replace(/^(thanks for applying to|thank you for (your interest in|applying to)|update on|update from|following up on your|in regards to|important information about|your application( for| at|—)?|[A-Z][\w&'.\- ]+ application status)\s*/i, "")
    .replace(/\s+at\s+[A-Z][\w&'.\- ]+$/i, "")
    .replace(/\s+role$/i, "")
    .trim();
  return cleaned || subject;
}
